/* METTA stock badges (snippets/inventory-badges.liquid) — product page and
   quick-add drawer, 2 Oct 2026.

   Each badge row carries a JSON map: variant id → [web, store, available]
   (the custom.iswebinstock / custom.isstoreinstock variant booleans). After
   variant interaction, only that product's rows read the selected variant
   id from their own form. Each injected map is parsed once, with no fetch.
   Delegated listeners also cover newly injected quick-view markup. */
(() => {
  const PRODUCT = '[data-is-full-product], [data-is-quick-view]';
  const maps = new WeakMap();
  const timers = new WeakMap();
  const SOLD_OUT = '<span class="inv-badge inv-badge--no inv-badge--soldout"><span class="inv-dot inv-dot--no"></span>Uppselt</span>';
  const badge = (ok, yes, no) =>
    ok
      ? `<span class="inv-badge inv-badge--ok"><span class="inv-dot inv-dot--ok"></span>${yes}</span>`
      : `<span class="inv-badge inv-badge--no"><span class="inv-dot inv-dot--no"></span>${no}</span>`;

  // The selected variant id of the product the row belongs to: the nearest
  // ancestor holding a cart form.
  const variantIdFor = (root) => {
    let el = root.parentElement;
    while (el && el !== document.body) {
      const field = el.querySelector('form[action*="/cart/add"] [name="id"]');
      if (field) return field.value;
      el = el.parentElement;
    }
    return null;
  };

  // No size picked yet (metta-pdp-size.js marks the section): the badges
  // speak for every size on sale, as the server drew them (4 Nov 2026).
  const unpicked = (root) => !!root.closest('[data-section-type="product"]')?.classList.contains('metta-needs-size');
  const anySize = (map) => {
    const onSale = Object.values(map).filter((v) => v[2]);
    if (!onSale.length) return [null, null, false];
    const any = (i) => (onSale.some((v) => v[i] === true) ? true : onSale.some((v) => v[i] === false) ? false : null);
    return [any(0), any(1), true];
  };

  const paint = (root) => {
    if (!root.isConnected) return;
    const id = unpicked(root) ? 'any' : variantIdFor(root);
    if (!id || id === root.dataset.variantId) return;
    const mapEl = root.querySelector('[data-inv-map]');
    if (!mapEl) return;
    let map = maps.get(mapEl);
    if (!map) {
      try { map = JSON.parse(mapEl.textContent); } catch (e) { return; }
      maps.set(mapEl, map);
    }
    if (id !== 'any' && !map[id]) return;
    root.dataset.variantId = id;

    const [web, store, available] = id === 'any' ? anySize(map) : map[id];
    let html = '';
    if (!available) html = SOLD_OUT;
    else {
      if (web !== null) html += badge(web, 'Til í netverslun', 'Ekki til í netverslun');
      if (store !== null) html += badge(store, 'Til í verslun Sunnusmára', 'Ekki til í verslun Sunnusmára');
    }
    // In place: the row was removed and re-inserted on every change, so the
    // line under the sizes blinked even when the stock read the same.
    const rows = root.querySelectorAll('.inv-badges');
    if (html && rows.length === 1) {
      if (rows[0].innerHTML !== html) rows[0].innerHTML = html;
      return;
    }
    rows.forEach((n) => n.remove());
    if (html) mapEl.insertAdjacentHTML('beforebegin', `<div class="inv-badges">${html}</div>`);
  };

  // Keep the delayed fallback for app-driven option changes, but coalesce
  // the click/change pair and never scan other products for a local event.
  const soon = (event) => {
    const scope = event.target.closest?.(PRODUCT);
    if (!scope && event.type !== 'product:variant-change') return;
    const root = scope || document;
    const previous = timers.get(root);
    if (previous) previous.forEach(clearTimeout);
    const repaint = () => root.querySelectorAll('[data-inv-badges]').forEach(paint);
    timers.set(root, [setTimeout(repaint, 0), setTimeout(repaint, 150)]);
  };

  document.addEventListener('click', soon, { passive: true });
  document.addEventListener('change', soon, { passive: true });
  document.addEventListener('product:variant-change', soon);
  // A colour swapped in can open with the size carried over: paint once
  // the size script has settled it.
  document.addEventListener('shopify:section:load', (event) => {
    setTimeout(() => event.target.querySelectorAll?.('[data-inv-badges]').forEach(paint), 200);
  });
})();
